import type { FC, PropsWithChildren } from "react";
import React, { useEffect, useState } from "react";

import useMembershipAcquisitionFunnel from "~/clientState/hooks/useMembershipAcquisitionFunnel";
import type { MembershipProductEvaluationMutationVariables } from "~/clientState/hooks/useMembershipProductEvaluationMutation";
import { useMembershipProductEvaluationMutation } from "~/clientState/hooks/useMembershipProductEvaluationMutation";
import { OnlineFunnel } from "~/constants/Funnel";
import { MembershipProductEvaluationCode } from "~/generated/graphql";
import { MembershipProductEvaluationStateContext } from "./contexts";
import {
  convertEvaluationParamToCode,
  formatCCDateFromYearAndMonth,
  isValidCreditCardBin,
  isValidCreditCardDate,
  isValidToken,
} from "./handlers/ProductEvaluationHandler";
import type {
  MembershipProductEvaluationOF1Type,
  MembershipProductEvaluationType,
  MembershipProductEvaluationOF1Variables,
} from "./types";
import useDispatchMembershipProductEvaluationCode from "~/clientState/providers/generic/PrimeEventManager/hooks/useDispatchMembershipProductEvaluationCode";

export const MembershipProductEvaluationOF1Provider: FC<
  PropsWithChildren<MembershipProductEvaluationOF1Type>
> = ({ listenEvaluationChanges, stopListenEvaluationChanges, children }) => {
  const [membershipEvaluationVariables, setMembershipEvaluationVariables] =
    useState<MembershipProductEvaluationOF1Variables>({
      code: MembershipProductEvaluationCode.NoRisk,
      creditCardBin: "",
    });

  useEffect(() => {
    listenEvaluationChanges((receivedEvaluationParams) => {
      if (typeof receivedEvaluationParams === "string") {
        setMembershipEvaluationVariables((oldValues) => ({
          ...oldValues,
          code: convertEvaluationParamToCode(receivedEvaluationParams),
        }));
      } else if (
        typeof receivedEvaluationParams === "object" &&
        receivedEvaluationParams !== null
      ) {
        setMembershipEvaluationVariables({
          code: convertEvaluationParamToCode(
            receivedEvaluationParams.membershipEvaluationCode ||
              MembershipProductEvaluationCode.NoRisk,
          ),
          creditCardBin: receivedEvaluationParams.creditCardBin || "",
        });
      }
    });
    return (): void => {
      stopListenEvaluationChanges();
    };
  }, [listenEvaluationChanges, stopListenEvaluationChanges]);
  return (
    <MembershipProductEvaluationStateContext.Provider
      value={{
        membershipEvaluationCode: membershipEvaluationVariables.code,
        creditCardBin: membershipEvaluationVariables.creditCardBin,
      }}
    >
      {children}
    </MembershipProductEvaluationStateContext.Provider>
  );
};

export const MembershipProductEvaluationProvider: FC<
  PropsWithChildren<MembershipProductEvaluationType>
> = ({
  children,
  funnel,
  paymentInstrumentToken,
  creditCardBin,
  expirationYear,
  expirationMonth,
}) => {
  const { isAcquisitionFunnel } = useMembershipAcquisitionFunnel({
    funnel: funnel || OnlineFunnel.FLIGHTS,
  });
  const { mutate, code } = useMembershipProductEvaluationMutation();
  const dispatchMembershipProductEvaluationCode =
    useDispatchMembershipProductEvaluationCode();
  const [
    currentMembershipEvaluationVariables,
    setCurrentMembershipEvaluationVariables,
  ] = useState<MembershipProductEvaluationMutationVariables>({
    paymentInstrumentToken: "",
    creditCardBin: "",
    creditCardDate: "",
  });

  useEffect(() => {
    if (
      isValidToken(
        paymentInstrumentToken,
        currentMembershipEvaluationVariables.paymentInstrumentToken,
      )
    ) {
      setCurrentMembershipEvaluationVariables((oldValues) => ({
        ...oldValues,
        paymentInstrumentToken,
      }));
    }
    //TODO: to be fixed by OPTIMUS
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInstrumentToken]);

  useEffect(() => {
    if (
      isValidCreditCardBin(
        creditCardBin,
        currentMembershipEvaluationVariables.creditCardBin,
      )
    ) {
      setCurrentMembershipEvaluationVariables((oldValues) => ({
        ...oldValues,
        creditCardBin,
      }));
    }
    //TODO: to be fixed by OPTIMUS
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditCardBin]);

  useEffect(() => {
    const creditCardDate = formatCCDateFromYearAndMonth(
      expirationYear,
      expirationMonth,
    );
    if (
      isValidCreditCardDate(
        creditCardDate,
        currentMembershipEvaluationVariables.creditCardDate,
      )
    ) {
      setCurrentMembershipEvaluationVariables((oldValues) => ({
        ...oldValues,
        creditCardDate,
      }));
    }
    //TODO: to be fixed by OPTIMUS
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expirationYear, expirationMonth]);

  const triggerEvaluation = (): void => {
    if (isAcquisitionFunnel) {
      mutate(currentMembershipEvaluationVariables);
    }
  };

  useEffect(() => {
    triggerEvaluation();

    //TODO: to be fixed by OPTIMUS
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembershipEvaluationVariables]);

  useEffect(() => {
    dispatchMembershipProductEvaluationCode(code);
  }, [code, dispatchMembershipProductEvaluationCode]);

  return (
    <MembershipProductEvaluationStateContext.Provider
      value={{
        membershipEvaluationCode: code,
        paymentInstrumentToken:
          currentMembershipEvaluationVariables.paymentInstrumentToken,
        creditCardBin: currentMembershipEvaluationVariables.creditCardBin,
      }}
    >
      {children}
    </MembershipProductEvaluationStateContext.Provider>
  );
};

export default MembershipProductEvaluationOF1Provider;
